import { ContactSupport, Dialog, DialogContent, DialogTitle, toast } from '@zawarski/eti-ui';
import { CONTACT_SUPPORT_API_URL } from '@constants/index';
import { useAccess } from '@hooks/useAccess';
import { useAppSelector } from '@store/hooks';
import { jwtTokenSelector } from '@store/app/app.selector';
import type { PalmettoAccount } from '@/types';

/** Who the support email says sent it: the signed-in user, their active group and positions. */
export const contactSupportSender = (
  account: PalmettoAccount | null | undefined,
  groupName: string,
) => ({
  name: `${account?.pvPersonGivenName || ''} ${account?.pvPersonSurName || ''}`.trim(),
  email: account?.email || '',
  group: groupName,
  positions: (account?.currentPositions ?? [])
    .map((position) => position.n)
    .filter((name): name is string => !!name),
});

interface ContactSupportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The side menu's Contact Support form: eti-ui's ContactSupport (subject, details, attachments)
 * in a dialog. It posts to the dashboard API's contact-support endpoint with REP's id_token, and
 * attachments go to the files API with the same token, as REP's other attachment areas do.
 * Failures render inside the form, so only a delivered message needs a toast here.
 */
const ContactSupportDialog = ({ open, onOpenChange }: ContactSupportDialogProps) => {
  const jwtToken = useAppSelector(jwtTokenSelector) || '';
  const { account, groupName, userId } = useAccess();
  const sender = contactSupportSender(account, groupName);

  const handleSent = () => {
    toast.success('Message sent to support.');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton className='rep:max-h-[90vh] rep:overflow-y-auto'>
        {/* ContactSupport draws its own heading; the dialog still needs a title for screen readers. */}
        <DialogTitle className='rep:sr-only'>Contact Support</DialogTitle>
        {/* Mounted only while open, so every visit starts a fresh form and attachment group. */}
        {open ? (
          <ContactSupport
            cognitoToken={jwtToken}
            fileStoreToken={jwtToken}
            fileStoreUserId={userId}
            apiUrl={CONTACT_SUPPORT_API_URL}
            onSuccess={handleSent}
            {...sender}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default ContactSupportDialog;
